<template>
  <WithBackdrop
    :backdrop="data?.fields?.backdrop"
    :class="[$style.config, $style.wrap]"
    :style="data?.fields?.cssOverride"
    data-article-set-v="3.0.3"
  >
    <BaseContainer :max-width="1280">
      <div :class="$style.inner">
        <header :class="$style.header">
          <BaseHeading
            v-if="data?.fields?.header"
            size="h4"
            as="h2"
            :class="$style.heading"
          >
            <span v-html="data?.fields?.header" />
          </BaseHeading>
          <MarkedParser
            v-if="data?.fields?.copy"
            :md="data?.fields?.copy"
            :class="['u-fs-p1', $style.copy]"
          />
        </header>

        <div
          v-if="computedCuratedCollection.length"
          ref="swiperRef"
          :class="['swiper', $style.articles]"
        >
          <div class="swiper-wrapper">
            <div
              v-for="article in computedCuratedCollection"
              :key="article?.sys?.id"
              :class="[$style.slide, 'swiper-slide']"
            >
              <NuxtLinkLocale
                :to="localePath(`/news/${article?.fields?.path || ''}`)"
                target="_blank"
              >
                <div :class="$style.article">
                  <AspectRatio ratio="545x378">
                    <BasePicture
                      :src="`${article?.fields?.featuredImage?.fields?.file?.url}?fit=thumb&q=90`"
                      alt=""
                    />
                  </AspectRatio>
                  <div :class="$style.articleInfoWrap">
                    <div :class="$style.articleInfo">
                      <div :class="[$style.meta, 'u-type-p3']">
                        <span
                          v-if="
                            !article?.fields?.hidePublishedDateTime &&
                            article?.fields?.publishedDateTime
                          "
                        >
                          {{
                            format(
                              new Date(article.fields.publishedDateTime),
                              'LLL d, yyyy',
                            )
                          }}
                        </span>
                      </div>
                      <BaseHeading as="h3" size="h6" :class="$style.title">
                        {{ article?.fields?.title }}
                      </BaseHeading>
                    </div>
                  </div>
                </div>
              </NuxtLinkLocale>
            </div>
          </div>
          <div :class="$style.buttons">
            <button
              :class="[$style.button, $style.prev]"
              aria-label="previous slide"
              :disabled="isAtBeginning"
              @click="prevSlide"
            >
              <IconCarouselArrowLeft :class="$style.icon" />
            </button>
            <button
              :class="[$style.button, $style.next]"
              aria-label="next slide"
              :disabled="isAtEnd"
              @click="nextSlide"
            >
              <IconCarouselArrowRight :class="$style.icon" />
            </button>
          </div>
        </div>
      </div>
    </BaseContainer>
  </WithBackdrop>
</template>

<script setup lang="ts">
import { format } from 'date-fns'
import type { Swiper } from 'swiper'
import swiper from 'swiper'
import 'swiper/css'

import type { TypeArticleSetWithoutUnresolvableLinksResponse } from '~/types/contentful'

const props = defineProps<{
  data: TypeArticleSetWithoutUnresolvableLinksResponse
}>()

const localePath = useLocalePath()
const { $visibility } = useNuxtApp()

const swiperRef = ref<(HTMLDivElement & { swiper: Swiper }) | null>(null)
const swiperInstance = ref<Swiper | null>(null)
const isAtBeginning = ref(true)
const isAtEnd = ref(false)

const computedCuratedCollection = computed(() => {
  return (
    props.data?.fields?.curatedCollection
      ?.filter(instanceOfArticle)
      ?.filter((article) => $visibility(article?.fields?.visibility)) || []
  )
})

const prevSlide = () => {
  swiperRef.value?.swiper.slidePrev()
}

const nextSlide = () => {
  swiperRef.value?.swiper.slideNext()
}

const syncNavState = () => {
  const instance = swiperInstance.value
  if (!instance) {
    isAtBeginning.value = true
    isAtEnd.value = false
    return
  }

  isAtBeginning.value = instance.isBeginning
  isAtEnd.value = instance.isEnd
}

const initSwiper = async () => {
  await nextTick()
  if (!swiperRef.value || swiperInstance.value) return

  swiperInstance.value = new swiper(swiperRef.value, {
    // slidesPerView: 1.25,
    slidesPerView: 'auto',
    slidesPerGroup: 1,
    spaceBetween: 20,
  })

  swiperInstance.value.on('slideChange', syncNavState)
  swiperInstance.value.on('update', syncNavState)
  swiperInstance.value.on('resize', syncNavState)
  syncNavState()
}

onMounted(async () => {
  if (computedCuratedCollection.value.length > 0) {
    await initSwiper()
  }
})

watch(
  () => computedCuratedCollection.value.length,
  async (count) => {
    if (count > 0 && !swiperInstance.value) {
      await initSwiper()
    }
    if (count === 0 && swiperInstance.value) {
      swiperInstance.value.destroy(true, true)
      swiperInstance.value = null
      syncNavState()
    }
  },
  { flush: 'post' },
)

onBeforeUnmount(() => {
  swiperInstance.value?.destroy(true, true)
  swiperInstance.value = null
  syncNavState()
})
</script>

<style lang="scss" module>
.config {
  @include breakpoint-variables(
    '--article-set-wrap-padding',
    (
      base: 45px 0,
      md: 60px 0,
      lg: 90px 0,
    )
  );

  @include breakpoint-variables(
    '--article-set-article-info-padding',
    (
      base: 15px 20px,
      md: 20px 30px,
    )
  );

  @include breakpoint-variables(
    '--article-set-article-slide-width',
    (
      base: 320px,
      xs: 350px,
      sm: 380px,
      md: 420px,
      lg: 500px,
    )
  );
}

.wrap {
  padding: var(--article-set-wrap-padding);
}

.inner {
  display: block;
}

.header {
  text-align: left;
  margin-bottom: 24px;
  max-width: 720px;
}

.heading {
  margin-bottom: 12px;
}

.copy {
  margin-bottom: 0;
}

.articles {
  width: 100%;
  overflow: visible;

  .slide {
    width: var(--article-set-article-slide-width);
  }
}

.buttons {
  display: flex;
  gap: 10px;
  margin-top: 15px;

  @include media('xs') {
    gap: 15px;
    margin-top: 20px;
  }
}

.button {
  z-index: 1;
  background: transparent;
  border: 0;
  padding: 0;
  color: var(--purple);

  display: flex;
  align-items: center;
  justify-content: center;
  width: min-content;
  height: min-content;

  &:hover {
    color: var(--purple-dark);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.icon {
  height: 12px;
  width: 24px;

  @include media('xs') {
    height: 17px;
    width: 34px;
  }
}

.meta {
  text-transform: uppercase;
  margin-bottom: 5px;
}

.title {
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-bottom: 3px;
}

.meta,
.title {
  color: var(--white);
}

.article {
  position: relative;
}

.articleInfoWrap {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  display: flex;
  padding: var(--article-set-article-info-padding);
  background: linear-gradient(to top, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0) 60%);
}

.articleInfo {
  margin-top: auto;
}
</style>
